import { useState, useMemo } from 'react';
import { cases } from '../../../mocks/cases';

// ── 地区配置 ──
interface RegionConfig {
  label: string;
  englishLabel: string;
  color: string;
  bgColor: string;
  borderColor: string;
  darkBg: string;
  x: number; // SVG坐标 (百分比)
  y: number;
  width: number;
  height: number;
  flag: string;
}

const REGION_CONFIGS: Record<string, RegionConfig> = {
  '美国': {
    label: '美国', englishLabel: 'United States', color: '#7C3AED', bgColor: 'bg-violet-50', borderColor: 'border-violet-200', darkBg: 'dark:bg-violet-900/20',
    x: 12, y: 28, width: 18, height: 14, flag: '🇺🇸',
  },
  '欧盟': {
    label: '欧盟', englishLabel: 'European Union', color: '#2563EB', bgColor: 'bg-sky-50', borderColor: 'border-sky-200', darkBg: 'dark:bg-sky-900/20',
    x: 44, y: 22, width: 14, height: 12, flag: '🇪🇺',
  },
  '香港': {
    label: '香港', englishLabel: 'Hong Kong', color: '#DC2626', bgColor: 'bg-red-50', borderColor: 'border-red-200', darkBg: 'dark:bg-red-900/20',
    x: 72, y: 35, width: 8, height: 8, flag: '🇭🇰',
  },
  '新加坡': {
    label: '新加坡', englishLabel: 'Singapore', color: '#D97706', bgColor: 'bg-amber-50', borderColor: 'border-amber-200', darkBg: 'dark:bg-amber-900/20',
    x: 70, y: 50, width: 8, height: 8, flag: '🇸🇬',
  },
  '中国大陆': {
    label: '中国大陆', englishLabel: 'Mainland China', color: '#B91C1C', bgColor: 'bg-rose-50', borderColor: 'border-rose-200', darkBg: 'dark:bg-rose-900/20',
    x: 65, y: 28, width: 12, height: 12, flag: '🇨🇳',
  },
  '澳大利亚': {
    label: '澳大利亚', englishLabel: 'Australia', color: '#059669', bgColor: 'bg-emerald-50', borderColor: 'border-emerald-200', darkBg: 'dark:bg-emerald-900/20',
    x: 72, y: 62, width: 12, height: 12, flag: '🇦🇺',
  },
  '全球': {
    label: '全球', englishLabel: 'Global', color: '#6B7280', bgColor: 'bg-gray-50', borderColor: 'border-gray-200', darkBg: 'dark:bg-gray-800',
    x: 45, y: 45, width: 10, height: 8, flag: '🌐',
  },
  '拉美': {
    label: '拉美', englishLabel: 'Latin America', color: '#16A34A', bgColor: 'bg-green-50', borderColor: 'border-green-200', darkBg: 'dark:bg-green-900/20',
    x: 22, y: 55, width: 10, height: 12, flag: '🌎',
  },
  '中东': {
    label: '中东', englishLabel: 'Middle East', color: '#CA8A04', bgColor: 'bg-yellow-50', borderColor: 'border-yellow-200', darkBg: 'dark:bg-yellow-900/20',
    x: 52, y: 35, width: 10, height: 10, flag: '🕌',
  },
  '非洲': {
    label: '非洲', englishLabel: 'Africa', color: '#EA580C', bgColor: 'bg-orange-50', borderColor: 'border-orange-200', darkBg: 'dark:bg-orange-900/20',
    x: 46, y: 48, width: 10, height: 14, flag: '🌍',
  },
};

// 地区归一化
function normalizeRegion(region: string): string {
  if (region.includes('美国') || region.includes('百慕大')) return '美国';
  if (region.includes('欧盟') || region.includes('英国') || region.includes('卢森堡') || region.includes('塞舌尔')) return '欧盟';
  if (region.includes('香港')) return '香港';
  if (region.includes('新加坡')) return '新加坡';
  if (region.includes('中国大陆')) return '中国大陆';
  if (region.includes('澳大利亚')) return '澳大利亚';
  if (region.includes('拉美') || region.includes('巴西') || region.includes('墨西哥')) return '拉美';
  if (region.includes('中东') || region.includes('迪拜') || region.includes('阿布扎比')) return '中东';
  if (region.includes('非洲') || region.includes('肯尼亚') || region.includes('尼日利亚')) return '非洲';
  if (region.includes('全球') || region.includes('香港/新加坡')) return '全球';
  return '全球';
}

// 统计各地区案例
function buildRegionStats() {
  const stats: Record<string, { count: number; types: Record<string, number>; cases: typeof cases }> = {};
  cases.forEach((c) => {
    const region = normalizeRegion(c.region);
    if (!stats[region]) stats[region] = { count: 0, types: {}, cases: [] };
    stats[region].count++;
    stats[region].types[c.type] = (stats[region].types[c.type] || 0) + 1;
    stats[region].cases.push(c);
  });
  return stats;
}

const TYPE_COLORS: Record<string, string> = {
  债券: '#7C3AED',
  房地产: '#10B981',
  大宗商品: '#F59E0B',
  股权: '#EC4899',
  碳信用: '#14B8A6',
  供应链信贷: '#F97316',
  其他: '#6B7280',
};

interface CaseMapProps {
  onClose: () => void;
}

export default function CaseMap({ onClose }: CaseMapProps) {
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [hoveredRegion, setHoveredRegion] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<string>('全部');

  const regionStats = useMemo(() => buildRegionStats(), []);

  const allRegions = Object.keys(REGION_CONFIGS).filter((r) => regionStats[r]);
  const maxCount = Math.max(...allRegions.map((r) => regionStats[r]?.count || 0));

  const selectedCases = useMemo(() => {
    if (!selectedRegion) return [];
    const regionCases = regionStats[selectedRegion]?.cases || [];
    if (filterType === '全部') return regionCases;
    return regionCases.filter((c) => c.type === filterType);
  }, [selectedRegion, filterType, regionStats]);

  const selectedTypes = useMemo(() => {
    if (!selectedRegion) return [];
    const types = regionStats[selectedRegion]?.types || {};
    return Object.entries(types).sort((a, b) => b[1] - a[1]);
  }, [selectedRegion, regionStats]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4 py-6">
      <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#2E1065] to-[#4C1D95] px-6 py-4 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl">
              <i className="ri-map-2-line text-white text-xl"></i>
            </div>
            <div>
              <div className="text-white font-black text-lg">全球 RWA 案例地图</div>
              <div className="text-white/60 text-xs">点击地区查看详细案例分布</div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/25 cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-white"></i>
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* 左侧：地图 */}
          <div className="flex-1 p-5 overflow-y-auto">
            {/* 统计概览 */}
            <div className="grid grid-cols-4 gap-3 mb-5">
              {[
                { label: '覆盖地区', value: allRegions.length, icon: 'ri-map-pin-line', color: '#7C3AED' },
                { label: '总案例数', value: cases.length, icon: 'ri-folder-3-line', color: '#F59E0B' },
                { label: '最大市场', value: '美国', icon: 'ri-building-2-line', color: '#10B981' },
                { label: '资产类型', value: '7类', icon: 'ri-grid-line', color: '#F97316' },
              ].map((s) => (
                <div key={s.label} className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3 text-center border border-gray-100 dark:border-gray-700">
                  <div className="w-7 h-7 flex items-center justify-center rounded-lg mx-auto mb-1.5" style={{ background: `${s.color}18` }}>
                    <i className={`${s.icon} text-xs`} style={{ color: s.color }}></i>
                  </div>
                  <div className="font-black text-gray-800 dark:text-gray-100 text-lg leading-none">{s.value}</div>
                  <div className="text-gray-500 dark:text-gray-400 text-xs mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>

            {/* 地区气泡图 */}
            <div className="relative bg-gradient-to-br from-[#F0F4FF] to-[#F8F7FF] dark:from-gray-800 dark:to-gray-850 rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden mb-5" style={{ height: 320 }}>
              {/* 装饰网格 */}
              <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'linear-gradient(#7C3AED22 1px, transparent 1px), linear-gradient(90deg, #7C3AED22 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

              {/* 大洲标签 */}
              <div className="absolute top-3 left-4 text-xs text-gray-400 dark:text-gray-500 font-medium">北美洲</div>
              <div className="absolute top-3 left-[42%] text-xs text-gray-400 dark:text-gray-500 font-medium">欧洲</div>
              <div className="absolute top-3 right-[28%] text-xs text-gray-400 dark:text-gray-500 font-medium">亚洲</div>
              <div className="absolute bottom-3 left-[20%] text-xs text-gray-400 dark:text-gray-500 font-medium">南美洲</div>
              <div className="absolute bottom-3 right-[22%] text-xs text-gray-400 dark:text-gray-500 font-medium">大洋洲</div>

              {/* 地区气泡 */}
              {allRegions.map((region) => {
                const config = REGION_CONFIGS[region];
                const count = regionStats[region]?.count || 0;
                const size = 36 + (count / maxCount) * 52;
                const isSelected = selectedRegion === region;
                const isHovered = hoveredRegion === region;

                return (
                  <button
                    key={region}
                    onClick={() => setSelectedRegion(isSelected ? null : region)}
                    onMouseEnter={() => setHoveredRegion(region)}
                    onMouseLeave={() => setHoveredRegion(null)}
                    className="absolute flex flex-col items-center cursor-pointer transition-all duration-200 group"
                    style={{
                      left: `${config.x}%`,
                      top: `${config.y}%`,
                      transform: 'translate(-50%, -50%)',
                      zIndex: isSelected || isHovered ? 10 : 1,
                    }}
                  >
                    {/* 气泡 */}
                    <div
                      className="rounded-full flex flex-col items-center justify-center transition-all duration-200 border-2"
                      style={{
                        width: size,
                        height: size,
                        background: isSelected
                          ? config.color
                          : `${config.color}22`,
                        borderColor: isSelected ? config.color : `${config.color}55`,
                        transform: isHovered || isSelected ? 'scale(1.12)' : 'scale(1)',
                        boxShadow: isSelected ? `0 0 20px ${config.color}55` : 'none',
                      }}
                    >
                      <span className="text-base leading-none">{config.flag}</span>
                      <span
                        className="font-black text-xs leading-none mt-0.5"
                        style={{ color: isSelected ? 'white' : config.color }}
                      >
                        {count}
                      </span>
                    </div>
                    {/* 标签 */}
                    <div
                      className="mt-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap"
                      style={{
                        background: isSelected ? config.color : 'white',
                        color: isSelected ? 'white' : config.color,
                        border: `1px solid ${config.color}44`,
                      }}
                    >
                      {config.label}
                    </div>
                  </button>
                );
              })}

              {/* 提示 */}
              {!selectedRegion && (
                <div className="absolute bottom-3 right-3 flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500">
                  <i className="ri-cursor-line text-xs"></i>
                  点击气泡查看案例
                </div>
              )}
            </div>

            {/* 图例 */}
            <div className="flex flex-wrap gap-2">
              {Object.entries(TYPE_COLORS).map(([type, color]) => (
                <div key={type} className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: color }}></span>
                  {type}
                </div>
              ))}
            </div>
          </div>

          {/* 右侧：地区详情 */}
          <div className="w-80 border-l border-gray-100 dark:border-gray-700 flex flex-col overflow-hidden">
            {!selectedRegion ? (
              /* 未选中：显示排行 */
              <div className="p-5 overflow-y-auto">
                <div className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
                  <i className="ri-bar-chart-2-line text-[#7C3AED]"></i>
                  地区案例排行
                </div>
                <div className="space-y-3">
                  {allRegions
                    .sort((a, b) => (regionStats[b]?.count || 0) - (regionStats[a]?.count || 0))
                    .map((region, idx) => {
                      const config = REGION_CONFIGS[region];
                      const count = regionStats[region]?.count || 0;
                      const pct = Math.round((count / cases.length) * 100);
                      return (
                        <button
                          key={region}
                          onClick={() => setSelectedRegion(region)}
                          className="w-full text-left group cursor-pointer"
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <span className="w-5 h-5 flex items-center justify-center rounded-full text-xs font-black shrink-0"
                              style={{ background: `${config.color}18`, color: config.color }}>
                              {idx + 1}
                            </span>
                            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 group-hover:text-[#7C3AED] transition-colors">
                              {config.flag} {config.label}
                            </span>
                            <span className="ml-auto text-xs font-black" style={{ color: config.color }}>{count}个</span>
                          </div>
                          <div className="ml-7 h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all duration-500"
                              style={{ width: `${pct}%`, background: config.color }}
                            ></div>
                          </div>
                          <div className="ml-7 mt-0.5 text-xs text-gray-400 dark:text-gray-500">{pct}% 占比</div>
                        </button>
                      );
                    })}
                </div>
              </div>
            ) : (
              /* 已选中：显示地区详情 */
              <div className="flex flex-col h-full overflow-hidden">
                {/* 地区头部 */}
                <div className="p-4 border-b border-gray-100 dark:border-gray-700 shrink-0">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{REGION_CONFIGS[selectedRegion]?.flag}</span>
                      <div>
                        <div className="font-black text-gray-800 dark:text-gray-100 text-base">{selectedRegion}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{REGION_CONFIGS[selectedRegion]?.englishLabel}</div>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedRegion(null)}
                      className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 cursor-pointer transition-colors"
                    >
                      <i className="ri-arrow-left-line text-gray-500 dark:text-gray-400 text-sm"></i>
                    </button>
                  </div>

                  {/* 统计 */}
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-2.5 text-center">
                      <div className="text-xl font-black" style={{ color: REGION_CONFIGS[selectedRegion]?.color }}>
                        {regionStats[selectedRegion]?.count}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">案例总数</div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-2.5 text-center">
                      <div className="text-xl font-black text-gray-800 dark:text-gray-100">
                        {selectedTypes.length}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">资产类型</div>
                    </div>
                  </div>

                  {/* 类型分布 */}
                  <div className="space-y-1.5 mb-3">
                    {selectedTypes.map(([type, count]) => (
                      <div key={type} className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: TYPE_COLORS[type] || '#6B7280' }}></span>
                        <span className="text-xs text-gray-600 dark:text-gray-400 flex-1">{type}</span>
                        <div className="flex items-center gap-1.5">
                          <div className="w-16 h-1 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full"
                              style={{
                                width: `${(count / (regionStats[selectedRegion]?.count || 1)) * 100}%`,
                                background: TYPE_COLORS[type] || '#6B7280',
                              }}
                            ></div>
                          </div>
                          <span className="text-xs font-bold text-gray-700 dark:text-gray-300 w-4 text-right">{count}</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* 类型筛选 */}
                  <div className="flex flex-wrap gap-1">
                    <button
                      onClick={() => setFilterType('全部')}
                      className={`text-xs px-2 py-0.5 rounded-full cursor-pointer transition-all whitespace-nowrap ${
                        filterType === '全部' ? 'bg-[#7C3AED] text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      全部
                    </button>
                    {selectedTypes.map(([type]) => (
                      <button
                        key={type}
                        onClick={() => setFilterType(type)}
                        className={`text-xs px-2 py-0.5 rounded-full cursor-pointer transition-all whitespace-nowrap ${
                          filterType === type ? 'text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                        style={filterType === type ? { background: TYPE_COLORS[type] || '#7C3AED' } : {}}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>

                {/* 案例列表 */}
                <div className="flex-1 overflow-y-auto p-4">
                  <div className="text-xs font-bold text-gray-500 dark:text-gray-400 mb-3">
                    {filterType === '全部' ? '全部案例' : filterType} · {selectedCases.length}个
                  </div>
                  <div className="space-y-2">
                    {selectedCases.map((c) => (
                      <div
                        key={c.id}
                        className="bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl p-3 hover:border-[#7C3AED]/30 transition-all"
                      >
                        <div className="flex items-start gap-2 mb-1.5">
                          <span
                            className="text-xs px-1.5 py-0.5 rounded-full font-medium shrink-0"
                            style={{ background: `${TYPE_COLORS[c.type] || '#6B7280'}18`, color: TYPE_COLORS[c.type] || '#6B7280' }}
                          >
                            {c.type}
                          </span>
                          <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium shrink-0 ${
                            c.status === '运营中' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400' :
                            c.status === '试点' ? 'bg-violet-50 text-violet-700 dark:bg-violet-900/20 dark:text-violet-400' :
                            c.status === '已发行' ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400' :
                            'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                          }`}>
                            {c.status}
                          </span>
                        </div>
                        <div className="text-xs font-bold text-gray-800 dark:text-gray-100 leading-snug mb-1 line-clamp-2">{c.name}</div>
                        <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
                          <span className="flex items-center gap-1">
                            <i className="ri-bar-chart-2-line text-[#7C3AED] text-xs"></i>
                            {c.tvl}
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-links-line text-xs"></i>
                            {c.platform.split('/')[0]}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
